import type { Mentionable as ChatMentionable, MentionActivity } from "@beacon/core/chat"
import type { Mentionable } from "@/markdown/mention-menu.tsx"
import { describeRelativeTime } from "@/time/describe.ts"
import { placeOf } from "./describe.ts"
import { messageHref } from "./message-place.ts"
import { plainLine } from "./unread.ts"
import { authorWords } from "./authors.ts"

/*
 * Mentions, as the web app writes, shows and lists them. The web API decides whom a mention tells
 * (the conversation's readers alone) and keeps each Member's; these say them, link to them, and
 * shape whom a conversation's messages may mention for the editor's `@` menu and its viewer.
 */

/** How many unread mentions, for a screen reader after a count: "3 unread mentions". */
export const describeMentionsUnread = (count: number) =>
  count <= 0 ? "" : count === 1 ? "1 unread mention" : `${count} unread mentions`

/**
 * Where a mention opens: the message itself, in its conversation, or a reply in its thread, beside
 * its conversation (`messageHref`).
 */
export const mentionHref = ({ message }: MentionActivity) => messageHref(message)

/** Who said the mentioning message, and what, as one plain line: "Ada: can you check?". */
export function mentionLineOf({ message }: MentionActivity) {
  const said =
    plainLine(message.body) ||
    (message.files.length === 1 ? "Shared a file" : `Shared ${message.files.length} files`)
  return `${authorWords(message.author)}: ${said}`
}

/**
 * Where a mention is, how it reached the Member, and when: "#general · in a thread · mentioned
 * @design · 5m ago".
 */
export function describeMention(
  { conversation, message, via, mentionedAt }: MentionActivity,
  now: Date,
) {
  const how =
    via.kind === "member"
      ? "mentioned you"
      : via.kind === "channel" || via.kind === "here"
        ? `mentioned @${via.kind}`
        : via.group
          ? `mentioned @${via.group.name}`
          : "mentioned a group you're in"
  return [
    placeOf(conversation),
    message.threadRootId ? "in a thread" : undefined,
    how,
    describeRelativeTime(mentionedAt, now),
  ]
    .filter(Boolean)
    .join(" · ")
}

/** Beacon, as the `@` menu offers it to be asked. */
const beaconOffered: Mentionable = { id: "beacon", name: "Beacon", email: null, beacon: true }

/**
 * Whom a conversation's messages may mention, as its editor and viewer take it: `offered` to the
 * `@` menu (its readers but `me`, then its Groups, then `@channel` and `@here` with how many each
 * would tell, when `me` may say them), `asking` to the menu of the box that sends a new message
 * (the same with Beacon after the readers, since a message sent mentioning it asks it), and the
 * `names` of readers and `groups` a message's mentions show by. Nothing before it is known.
 */
export function mentionsShown(mentionable: ChatMentionable | undefined, me: string) {
  const members = mentionable?.members ?? []
  const groups = mentionable?.groups ?? []
  const readers = members.filter(({ id }) => id !== me)
  const rest: Mentionable[] = [
    ...groups.map(({ id, name, reaches }) => ({ id, name, email: null, group: { reaches } })),
    ...(["channel", "here"] as const).flatMap((kind) =>
      mentionable?.wholeChannel
        ? [
            {
              id: kind,
              name: kind,
              email: null,
              wholeChannel: { reaches: mentionable.wholeChannel[kind] },
            },
          ]
        : [],
    ),
  ]
  const offered: Mentionable[] = [...readers, ...rest]
  const asking: Mentionable[] = mentionable ? [...readers, beaconOffered, ...rest] : []
  return {
    offered,
    asking,
    names: new Map(members.map(({ id, name }) => [id, name])),
    groups: new Map(groups.map(({ id, name }) => [id, name])),
  }
}
